import { useCallback, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { setDirectorySportHeader, setNeedsBoardUser } from '../../services/api';
import type { DirectorySport } from '../../types/directory';
import { invalidatePageCache } from '../player-database/pagePrefetch';
import { DirectorySportContext, useOrganizationSport, type DirectorySportState } from './directorySportContext';
import { readStoredSport, writeStoredSport } from './directorySportStore';

/**
 * Holds a system admin's Baseball / Softball pick for the Recruiting Directory pages, remembered per
 * user in this browser, and hands it to `api.ts`, which sends it as `X-Directory-Sport` on every
 * `/api/directory/` request. Everyone else reads their program's sport and sends no header (the API
 * ignores one from anyone but a system admin anyway).
 *
 * It also tells `api.ts` who is signed in, so every directory request carries that user's needs board
 * as `X-Needs-Board` (Worth to you is weighed by it).
 */
export const DirectorySportProvider = ({ children }: { children: ReactNode }) => {
  const { user, isSystemAdmin, isImpersonating } = useAuth();
  const organizationSport = useOrganizationSport();
  const userId = user?.uid ?? null;
  // This session's picks by user, over what storage remembers, so a blocked storage still switches.
  const [picked, setPicked] = useState<Record<string, DirectorySport>>({});

  // Not while viewing another program: the admin then sees that program's sport, as its coaches do,
  // and nothing they add or save there lands under the other sport.
  const canSwitch = isSystemAdmin && !isImpersonating && userId !== null;
  const chosen = canSwitch ? (picked[userId] ?? readStoredSport(userId)) : null;
  const sport = chosen ?? organizationSport;

  // Written during render, not in an effect: a page's own load effects run before this provider's,
  // so an effect would let the first directory request of a page go out without the header.
  setDirectorySportHeader(canSwitch ? sport : null);
  // And again once committed, so a render React threw away cannot leave the header on its value.
  const header = canSwitch ? sport : null;
  useLayoutEffect(() => setDirectorySportHeader(header), [header]);
  setNeedsBoardUser(userId);
  useLayoutEffect(() => setNeedsBoardUser(userId), [userId]);

  const setSport = useCallback(
    (next: DirectorySport) => {
      if (!canSwitch || userId === null) return;
      writeStoredSport(userId, next);
      if (next === sport) return;
      // Cached pages were read for the other sport.
      invalidatePageCache();
      setDirectorySportHeader(next);
      setPicked((current) => ({ ...current, [userId]: next }));
    },
    [canSwitch, sport, userId]
  );

  const value = useMemo<DirectorySportState>(() => ({ sport, canSwitch, setSport }), [sport, canSwitch, setSport]);
  return <DirectorySportContext.Provider value={value}>{children}</DirectorySportContext.Provider>;
};
